import { functionEndpoint, isProxyConfigured, proxyHeaders } from '@/lib/proxy';

/**
 * Client side of the /support contact form. POSTs to the Supabase
 * `contact-form` edge function which forwards via Mailgun to the
 * support inbox. The Mailgun API key and the inbox address itself
 * stay server-side — clients never see either.
 *
 * Callers should treat a returned `ok: false` as recoverable: the UI
 * falls back to a mailto handoff so the user can still reach us even
 * if the Supabase function isn't deployed or Mailgun is down.
 */

export interface ContactFormInput {
  name: string;
  email: string;
  /** All four contact fields are mandatory on the client. */
  phone: string;
  topic: string;
  message: string;
  /** Free-form version string for the support team to triage by. */
  version?: string;
  /** Cloudflare Turnstile token — web submissions only; the server
   *  enforces it for platform "web" and ignores it elsewhere. */
  turnstile?: string;
  /** Platform.OS of the sender ("web" | "android" | "ios"). */
  platform?: string;
}

export interface ContactFormOk {
  ok: true;
}
export interface ContactFormErr {
  ok: false;
  error: { kind: string; message: string };
}
export type ContactFormResult = ContactFormOk | ContactFormErr;

/**
 * Submit a contact-form message. Returns ok:true on Mailgun acceptance,
 * ok:false otherwise. Network failures, validation failures, and
 * Mailgun upstream errors all surface as ok:false — caller decides
 * whether to retry or fall back.
 */
export async function sendContactForm(
  input: ContactFormInput
): Promise<ContactFormResult> {
  if (!isProxyConfigured()) {
    return {
      ok: false,
      error: { kind: 'no-proxy', message: 'Server endpoint not configured.' },
    };
  }
  try {
    const res = await fetch(functionEndpoint('contact-form'), {
      method: 'POST',
      headers: proxyHeaders(),
      body: JSON.stringify(input),
    });
    let payload: ContactFormResult | null = null;
    try {
      payload = (await res.json()) as ContactFormResult;
    } catch {
      // Function returned non-JSON. Treat as upstream error.
    }
    if (!res.ok || (payload && payload.ok === false)) {
      const errInfo =
        payload && 'error' in payload && payload.error
          ? payload.error
          : { kind: 'upstream', message: `HTTP ${res.status}` };
      return { ok: false, error: errInfo };
    }
    return { ok: true };
  } catch (e) {
    return {
      ok: false,
      error: { kind: 'network', message: e instanceof Error ? e.message : String(e) },
    };
  }
}
